(() => {
  const cache = new Map(), queue = [];
  let active = 0;
  const lang = (document.documentElement.lang || 'fr').slice(0, 2);
  const text = {fr:'Voir les avis',en:'Read reviews',de:'Bewertungen lesen'}[lang] || 'Voir les avis';
  function productURL(value) {
    const url = new URL(value, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) return null;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    url.pathname = root.replace(/\/$/, '') + url.pathname.replace(/^\/(?:en|de|fr)(?=\/)/, '');
    url.search = ''; url.hash = '';
    return url;
  }
  function pump() {
    while (active < 4 && queue.length) {
      const task = queue.shift(); active++;
      task().finally(() => { active--; pump(); });
    }
  }
  function load(url) {
    const key = url.pathname;
    if (!cache.has(key)) {
      cache.set(key, new Promise((resolve, reject) => {
        queue.push(async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 10000);
          try {
            const endpoint = new URL(url);
            endpoint.searchParams.set('section_id', 'mpd-rating-data');
            const preview = new URL(location.href).searchParams.get('preview_theme_id');
            if (preview) endpoint.searchParams.set('preview_theme_id', preview);
            const response = await fetch(endpoint, {credentials:'same-origin', signal:controller.signal});
            if (!response.ok) throw new Error('Rating unavailable');
            const html = new DOMParser().parseFromString(await response.text(), 'text/html');
            const rating = html.querySelector('.mpd-review-link');
            if (!rating) throw new Error('Rating unavailable');
            resolve(rating);
          } catch (error) { cache.delete(key); reject(error); }
          finally { clearTimeout(timeout); }
        });
        pump();
      }));
    }
    return cache.get(key);
  }
  // Fetch rating fragments when their cards approach the viewport, including dynamic results.
  const visibleRatings = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (!entry.isIntersecting) return;
      visibleRatings.unobserve(entry.target);
      const start = ratingTasks.get(entry.target);
      ratingTasks.delete(entry.target);
      if (start) start();
    });
  }, {rootMargin: '250px 0px'}) : null;
  const ratingTasks = new WeakMap();
  function hydrate(container, productLink) {
    if (!productLink || container.querySelector('.mpd-review-link') || container.dataset.mpdReviewPending) return;
    const url = productURL(productLink.getAttribute('href'));
    if (!url) return;
    container.dataset.mpdReviewPending = 'true';
    const fallback = document.createElement('a');
    fallback.className = 'mpd-review-link';
    fallback.href = url.pathname + '#looxReviews';
    fallback.textContent = text;
    fallback.setAttribute('aria-label', text + ' — ' + productLink.textContent.trim());
    if (container.classList.contains('mpd-pcard__body') || container.classList.contains('mpd-wd__in')) productLink.after(fallback);
    else container.append(fallback);
    const start = () => {
      if (!fallback.isConnected) return;
      load(url).catch(() => load(url)).then(rating => {
        if (fallback.isConnected) fallback.replaceWith(rating.cloneNode(true));
      }).catch(() => { /* The product-specific reviews link remains usable. */ });
    };
    if (visibleRatings) {
      ratingTasks.set(container, start);
      visibleRatings.observe(container);
    } else start();
  }
  function scan(root) {
    if (!(root instanceof Element || root instanceof Document)) return;
    const find = selector => [...(root.matches && root.matches(selector) ? [root] : []), ...root.querySelectorAll(selector)];
    find('.mpd-pcard__body,.mpd-wd__in').forEach(el => hydrate(el, el.querySelector('.mpd-pcard__name,.mpd-wd__nm')));
    find('a.mpd-predict__item,a.mpd-cx__card').forEach(link => {
      if (link.parentElement.dataset.mpdReviewWrapper) return;
      const wrapper = document.createElement('div');
      wrapper.dataset.mpdReviewWrapper = 'true';
      wrapper.className = link.classList.contains('mpd-predict__item') ? 'mpd-review-result' : 'mpd-review-card';
      link.before(wrapper); wrapper.append(link); hydrate(wrapper, link);
    });
  }
  scan(document);
  // Batch dynamic card updates; Loox's own DOM does not contain product cards.
  const pendingRoots = new Set();
  let scanScheduled = false;
  function flushCards() {
    scanScheduled = false;
    const roots = [...pendingRoots]; pendingRoots.clear();
    roots.forEach(root => {
      if (root.isConnected && !roots.some(parent => parent !== root && parent.contains(root))) scan(root);
    });
  }
  const observer = new MutationObserver(records => {
    records.forEach(record => {
      if (record.target instanceof Element && record.target.closest('#looxReviews,.mpd-review-backup,.loox-rating,.mpd-review-link')) return;
      record.addedNodes.forEach(node => {
        if (node.nodeType !== 1 || node.matches('script,style,link,iframe,.loox-rating,.mpd-review-link,.mpd-review-link *,#looxReviews,.mpd-review-backup')) return;
        if (node.matches('.mpd-pcard__body,.mpd-wd__in,a.mpd-predict__item,a.mpd-cx__card') || node.querySelector('.mpd-pcard__body,.mpd-wd__in,a.mpd-predict__item,a.mpd-cx__card')) pendingRoots.add(node);
      });
    });
    if (pendingRoots.size && !scanScheduled) {
      scanScheduled = true;
      requestAnimationFrame(flushCards);
    }
  });
  observer.observe(document.body, {childList:true,subtree:true});
  document.addEventListener('click', event => {
    const link = event.target.closest('.mpd-review-link[href="#looxReviews"]');
    if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    const reviews = document.getElementById('looxReviews');
    if (!reviews) return;
    event.preventDefault();
    reviews.setAttribute('tabindex','-1');
    reviews.focus({preventScroll:true});
    reviews.scrollIntoView({behavior:matchMedia('(prefers-reduced-motion: reduce)').matches?'auto':'smooth',block:'start'});
    history.replaceState(null,'','#looxReviews');
  });

  // Keep genuine server-rendered reviews visible until the interactive iframe
  // has loaded AND received its content height from Loox.
  const protectedWidgets = new WeakSet();
  function protectReviews(widget) {
    if (protectedWidgets.has(widget)) return;
    const feed = widget.querySelector('#loox-reviews-feed');
    const mainRating = document.querySelector('.mpd-review-link[href="#looxReviews"] .mpd-rating-summary, .mpd-review-link[href="#looxReviews"] .loox-rating');
    const isEmpty = mainRating && mainRating.dataset.id === widget.dataset.productId && Number(mainRating.dataset.raters) === 0;
    if (!feed && !isEmpty) return;
    protectedWidgets.add(widget);
    // Retain the original feed by reference, without duplicating its DOM/images.
    let backup = feed ? null : document.createElement('div');
    if (!feed) {
      const heading = document.createElement('h3');
      heading.textContent = {fr:'Avis clients',en:'Customer reviews',de:'Kundenbewertungen'}[lang] || 'Avis clients';
      const message = document.createElement('p');
      message.textContent = {fr:'Pas encore d’avis pour ce produit.',en:'No reviews yet for this product.',de:'Noch keine Bewertungen für dieses Produkt.'}[lang] || 'Pas encore d’avis pour ce produit.';
      backup.append(heading, message);
      backup.dataset.empty = 'true';
    }
    if (backup) {
      backup.className = 'mpd-review-backup';
      widget.after(backup);
    }
    if (!feed) { widget.setAttribute('data-mpd-review-loading', ''); widget.setAttribute('aria-busy', 'true'); }
    let frame = null, loaded = false, finished = false;
    const ready = () => {
      if (finished || !loaded || !frame || frame.getBoundingClientRect().height < 40) return;
      finished = true;
      widget.removeAttribute('data-mpd-review-loading');
      widget.removeAttribute('aria-busy');
      if (backup) backup.remove();
      changes.disconnect();
      sizes.disconnect();
    };
    const sizes = new ResizeObserver(ready);
    const update = () => {
      if (finished) return;
      const next = widget.querySelector('iframe');
      if (!next) return;
      if (next !== frame) {
        if (frame) sizes.unobserve(frame);
        frame = next;
        loaded = false;
        frame.addEventListener('load', () => { loaded = true; ready(); }, {once:true});
        sizes.observe(frame);
      }
      if (!feed || !widget.contains(feed)) {
        if (!backup) {
          backup = feed;
          backup.removeAttribute('id');
          backup.className = 'mpd-review-backup';
          backup.querySelectorAll('style,script').forEach(el => el.remove());
          backup.querySelectorAll('[id]').forEach(el => el.removeAttribute('id'));
          widget.after(backup);
        }
        backup.hidden = false;
        widget.setAttribute('data-mpd-review-loading', '');
        widget.setAttribute('aria-busy', 'true');
      }
      ready();
    };
    const changes = new MutationObserver(update);
    changes.observe(widget, {childList:true, subtree:true, attributes:true, attributeFilter:['height','style']});
    update();
  }
  document.querySelectorAll('#looxReviews').forEach(protectReviews);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('#looxReviews').forEach(protectReviews);
  });

  // Native fragment navigation positions incoming review links immediately.
  // Never jump back to the widget on window.load: third-party assets can finish
  // long after the visitor has started scrolling or reading another section.
})();

